import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Store as StoreIcon } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";
import ImageUpload from "@/components/common/ImageUpload";

const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "CAD", "AUD"];
const LANGUAGES = [["en", "English"], ["it", "Italiano"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"]];

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputCls =
  "h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand";

export default function MyStore() {
  const t = useT();
  const navigate = useNavigate();
  const { store, setStore, refresh } = useStore();
  const isEdit = !!store;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", logo_url: "", domain: "", category: "", language: "en", currency: "USD",
    company: { description: "", email: "", phone: "", address: "", vat: "" },
    status: "draft",
  });

  useEffect(() => {
    if (store) {
      setForm({
        name: store.name || "", logo_url: store.logo_url || "", domain: store.domain || "",
        category: store.category || "", language: store.language || "en", currency: store.currency || "USD",
        company: { description: "", email: "", phone: "", address: "", vat: "", ...(store.company || {}) },
        status: store.status || "draft",
      });
    }
  }, [store]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCompany = (k, v) => setForm((f) => ({ ...f, company: { ...f.company, [k]: v } }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/stores/${store.id}`, form);
      setStore(data);
      toast.success(t("store.updated"));
    } catch {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  const finishWizard = async (publish) => {
    setSaving(true);
    try {
      const { data } = await api.post("/stores", { ...form, status: publish ? "published" : "draft" });
      setStore(data);
      await refresh();
      toast.success(publish ? t("store.published") : t("store.created"));
      navigate("/dashboard/products");
    } catch {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  // -------- EDIT MODE --------
  if (isEdit) {
    return (
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("store.editTitle")}</h1>
        <div className="mt-8 space-y-5 rounded-lg border border-border bg-card p-6">
          <ImageUpload label={t("store.logo")} value={form.logo_url} onChange={(v) => set("logo_url", v)} testid="store-logo-upload" />
          <Field label={t("store.name")}><input data-testid="store-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t("store.category")}><input data-testid="store-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
            <Field label={t("store.domain")}><input data-testid="store-domain" className={inputCls} value={form.domain} onChange={(e) => set("domain", e.target.value)} placeholder={t("store.domainPlaceholder")} /></Field>
            <Field label={t("store.language")}>
              <select data-testid="store-language" className={inputCls} value={form.language} onChange={(e) => set("language", e.target.value)}>
                {LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label={t("store.currency")}>
              <select data-testid="store-currency" className={inputCls} value={form.currency} onChange={(e) => set("currency", e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("store.companyDescription")}>
            <textarea data-testid="store-company-desc" className={`${inputCls} h-24 py-2`} value={form.company.description} onChange={(e) => setCompany("description", e.target.value)} placeholder={t("store.companyDescriptionPlaceholder")} />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t("store.companyEmail")}><input className={inputCls} value={form.company.email} onChange={(e) => setCompany("email", e.target.value)} /></Field>
            <Field label={t("store.companyPhone")}><input className={inputCls} value={form.company.phone} onChange={(e) => setCompany("phone", e.target.value)} /></Field>
            <Field label={t("store.companyAddress")}><input className={inputCls} value={form.company.address} onChange={(e) => setCompany("address", e.target.value)} /></Field>
            <Field label={t("store.companyVat")}><input className={inputCls} value={form.company.vat} onChange={(e) => setCompany("vat", e.target.value)} /></Field>
          </div>
          <button onClick={saveEdit} disabled={saving} data-testid="store-save"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-6 font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95 disabled:opacity-60">
            {saving ? t("common.loading") : t("store.saveButton")}
          </button>
        </div>
      </div>
    );
  }

  // -------- WIZARD MODE --------
  const total = 3;
  const canNext = step === 1 ? form.name.trim().length > 0 : true;

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("store.wizardTitle")}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("store.step", { current: step, total })}</p>

      <div className="mt-6 flex gap-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-brand" : "bg-border"}`} />
        ))}
      </div>

      <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
        className="mt-8 space-y-5 rounded-lg border border-border bg-card p-6">
        {step === 1 && (
          <>
            <h2 className="font-display text-lg font-bold">{t("store.step1")}</h2>
            <ImageUpload label={t("store.logo")} value={form.logo_url} onChange={(v) => set("logo_url", v)} testid="wizard-logo-upload" />
            <Field label={t("store.name")}><input data-testid="wizard-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("store.namePlaceholder")} /></Field>
            <Field label={t("store.domain")} hint={t("store.domainPlaceholder")}><input data-testid="wizard-domain" className={inputCls} value={form.domain} onChange={(e) => set("domain", e.target.value)} placeholder={t("store.domainPlaceholder")} /></Field>
            <Field label={t("store.category")}><input data-testid="wizard-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} placeholder={t("store.categoryPlaceholder")} /></Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t("store.language")}>
                <select data-testid="wizard-language" className={inputCls} value={form.language} onChange={(e) => set("language", e.target.value)}>
                  {LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label={t("store.currency")}>
                <select data-testid="wizard-currency" className={inputCls} value={form.currency} onChange={(e) => set("currency", e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="font-display text-lg font-bold">{t("store.step2")}</h2>
            <Field label={t("store.companyDescription")}>
              <textarea data-testid="wizard-company-desc" className={`${inputCls} h-24 py-2`} value={form.company.description} onChange={(e) => setCompany("description", e.target.value)} placeholder={t("store.companyDescriptionPlaceholder")} />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t("store.companyEmail")}><input data-testid="wizard-company-email" className={inputCls} value={form.company.email} onChange={(e) => setCompany("email", e.target.value)} /></Field>
              <Field label={t("store.companyPhone")}><input className={inputCls} value={form.company.phone} onChange={(e) => setCompany("phone", e.target.value)} /></Field>
              <Field label={t("store.companyAddress")}><input className={inputCls} value={form.company.address} onChange={(e) => setCompany("address", e.target.value)} /></Field>
              <Field label={t("store.companyVat")}><input className={inputCls} value={form.company.vat} onChange={(e) => setCompany("vat", e.target.value)} /></Field>
            </div>
          </>
        )}
        {step === 3 && (
          <div className="py-4 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand/10"><StoreIcon className="h-6 w-6 text-brand" /></div>
            <h2 className="mt-4 font-display text-xl font-bold">{t("store.publishTitle")}</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{t("store.publishBody")}</p>
          </div>
        )}
      </motion.div>

      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="wizard-prev"
          className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-secondary disabled:opacity-40">
          <ArrowLeft className="h-4 w-4" />{t("common.previous")}
        </button>
        {step < total ? (
          <button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="wizard-next"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95 disabled:opacity-40">
            {t("common.next")}<ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => finishWizard(false)} disabled={saving} data-testid="wizard-create"
              className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-medium transition-colors hover:bg-secondary disabled:opacity-60">
              {t("store.createButton")}
            </button>
            <button onClick={() => finishWizard(true)} disabled={saving} data-testid="wizard-publish"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95 disabled:opacity-60">
              <Check className="h-4 w-4" />{t("store.publishButton")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
